/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IOrderSummaryErrors } from '@msdyn365-commerce-modules/cart/src/modules/cart/./cart';
import { ICartLineItemViewProps } from '@msdyn365-commerce-modules/cart/src/modules/cart/./components//cart-line-items-promotion';
import { ICartlinesViewProps } from '@msdyn365-commerce-modules/cart/src/modules/cart/./components/cart-line-items';
import { Button, format, INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import get from 'lodash/get';
import Stepper from 'mfcl/Stepper';
import * as React from 'react';
import Cookies from 'universal-cookie';
/* VSI Customization - Start - */
import { INotification } from '../../data-actions/mfrm-notification.action';
// import { IMfrmNotifications } from './mfrm-cart.da';
// import PromoCodeComponent from './components/promocode.component';
import PromoCodeComponent from '../../themes/mattressfirm/views/components/promocode.component';
import { IOrderSummaryLines } from '../../Utilities/order-summary-lineitem';
import { ICartViewProps } from './mfrm-cart';
import { IMfrmCartData } from './mfrm-cart.data';
import { IMfrmCartProps } from './mfrm-cart.props.autogenerated';
import _getViewPort from '../../Utilities/get-view-port';


export interface ICartlinesProps extends ICartlinesViewProps {
    outOfStock?:  React.ReactNode | undefined;
    errorDelivery?: React.ReactNode | undefined;
    QuantityMessage?: React.ReactNode | undefined;
}

export interface IDeliveryChildService {
    addKeyInPrice:  boolean;
    excludeZipCode: boolean;
    redeliveryMessage: string;
    active?: boolean;
    productRectId?: number;
    serviceName?: string;
    serviceSKU?: string;
    zipCode?: string;
    alternativeSolution: boolean;
}

export interface IDeliveryProps {
    childService: IDeliveryChildService[];
    DefaultStateWithDeliveryExpanded: boolean;
    active: boolean;
    adjustableBase: string;
    boxSpring: string;
    callOrTextMessage: string;
    defaultStateWithWillCallExpanded: boolean;
    descriptionMessage: string;
    displayServiceSku: boolean;
    mattresses: string;
    redeliveryMessage: string;
    serviceId: number;
    serviceName: string;
    serviceProductRecId: number;
    serviceSku: string;
    switchableServiceIds: string;
    warningMessage: string;
    willCallMessage: string;
    addKeyInPrice?: boolean;
}

/* VSI Customization - End - */

const _renderCartlines = (cartLines: ICartlinesProps[] | undefined, cartEmptyText: string, CartlinesWrapper: INodeProps, storeSelector: React.ReactNode | undefined,
    backToShoppingButton: React.ReactNode, waitingComponent: React.ReactNode, cartLoadingStatus: string, cartDataResult: boolean, productCollectionSlot: React.ReactNode[] , deliveryServices:IDeliveryProps[]): JSX.Element[] | JSX.Element => {
    if (cartLoadingStatus) {
        return <>{cartLoadingStatus}</>;
    }
    if (cartLines) {
        return cartLines.map((cartLine) => {
            /** VSI Customization - Start - 21-12-2020 */
            /** VSI RecordId of product data is added instead of index to fix the outline issue on remove icon */
            const recordId = cartLine.data && cartLine.data.product && cartLine.data.product.RecordId;
            const checkExist = !!deliveryServices.find((val: {serviceProductRecId: number}) => val?.serviceProductRecId === recordId);
            return (
                // tslint:disable: jsx-key
                /* eslint-disable react/jsx-key */
                <div>
                    {cartLine.QuantityMessage}
                    <div className={cartLine.outOfStock || cartLine.errorDelivery ? 'msc-cart-lines-item cart-product-alert' : checkExist ? 'msc-cart-lines-item hidden' : 'msc-cart-lines-item'} key={recordId}>
                        {cartLine.cartline}
                        {
                            cartLine.addToOrderTemplate ? (
                                <div className='msc-cart-line__extra-actions'>
                                    {cartLine.remove}
                                    {/* VSI Customization - Hide add to wishlist button */}
                                    {/* {cartLine.addToWishlist} */}
                                    {/* {cartLine.addToOrderTemplate} */}
                                </div>
                            ) : (
                                <>
                                    {cartLine.remove}
                                    {/* {cartLine.addToWishlist} */}
                                    {/* VSI Customization End - Hide add to wishlist button */}
                                </>
                            )
                        }
                    </div>
                    {cartLine.outOfStock}
                    {cartLine.errorDelivery}

                </div>
            );
            /** VSI Customization - End - 21-12-2020 */
        });
    } else {
        return (cartDataResult ?
            (
                <>
                    <div tabIndex={0} className='msc-cart__empty-cart'>
                        <div tabIndex={-1}>
                            <p className='msc-cart-line'>{cartEmptyText}</p>
                            {backToShoppingButton}
                        </div>
                    </div>
                    {productCollectionSlot && productCollectionSlot.length > 0 &&
                        productCollectionSlot.map((children: React.ReactNode, index: number) => {
                            return (
                                <div key={index}>
                                    {children}
                                </div>
                            );
                        })}
                </>
            ) : <>{waitingComponent}</>
        );
    }
};

const _renderErrorBlock = (errorData: IOrderSummaryErrors | undefined): JSX.Element | null => {
    if (!errorData || errorData.errors.length === 0) {
        return null;
    }
    return (
        <Node {...errorData.Wrapper}>
            {errorData.header}
            {errorData.errors}
        </Node>
    );
};

const getTotalPrice = (orderSummaryLines: IOrderSummaryLines | undefined) => {
    const delivery = get(orderSummaryLines?.deliveryFee, 'props.value') || 0;
    const price = get(orderSummaryLines?.orderTotal, 'props.price');
    return parseFloat(price) + getSavings(orderSummaryLines) - getTax(orderSummaryLines) - _parseRecyclingFee(orderSummaryLines) - delivery;
};
const getSavings = (orderSummaryLines: IOrderSummaryLines | undefined) => {
    const savings = get(orderSummaryLines?.totalDiscounts, 'props.price');
    if(savings) {
        return parseFloat(Math.abs(savings).toFixed(2));
    }
    return 0;
};

const getFinalPrice = (orderSummaryLines: IOrderSummaryLines | undefined, mattressRecyclingFee?: number, props?: ICartViewProps) => {
    const totalPrice = getTotalPrice(orderSummaryLines);
    const savings = getSavings(orderSummaryLines);
    const tax = props?.calculateTaxOnCart ? getTax(orderSummaryLines) : 0.0;
    const finalPrice = totalPrice - (savings ? savings : 0) + (tax ? tax : 0) + (props?.calculateTaxOnCart ? (mattressRecyclingFee ? mattressRecyclingFee : 0) : 0);
    return parseFloat(finalPrice.toFixed(2));
};

const getTax = (orderSummaryLines: IOrderSummaryLines | undefined) => {
    let price = get(orderSummaryLines?.tax, 'props.price');
    price = price - _parseRecyclingFee(orderSummaryLines);
    return price ? parseFloat(price) : 0;
};

const _getRecyclingFee = (orderSummaryLines: IOrderSummaryLines | undefined) => {
    const label = get(orderSummaryLines?.mattressRecyclingFee, 'props.label');
    const value = get(orderSummaryLines?.mattressRecyclingFee, 'props.value');
    return {label, value};
};
// const _getDeliveryFee = (orderSummaryLines: IOrderSummaryLines | undefined) => {
//     const label = get(orderSummaryLines?.deliveryFee, 'props.label');
//     const value = get(orderSummaryLines?.deliveryFee, 'props.value');
//     return {label, value};
// };
let yetToBeCalculated = '';
let recyclingFeeCart = 0;
const _parseRecyclingFee = (orderSummaryLines: IOrderSummaryLines | undefined) => {
    const value = get(orderSummaryLines?.mattressRecyclingFee, 'props.value');
    return value.toLowerCase() === yetToBeCalculated ? (recyclingFeeCart || 0) : Number(value.split('$')[1]);
};

const formatNumber = (num: number) => {
    return parseFloat(num.toFixed(2)).toLocaleString('en-US', {
                            style: 'currency',
                            currency: 'USD'
                    });
};

const getCartItemsQuantity = (cartLines?: ICartlinesViewProps[]): number => {
    let totalItems = 0;
    if(cartLines) {
        for(const cl of cartLines) {
            totalItems = totalItems + (cl.data?.cartline.Quantity ? cl.data?.cartline.Quantity : 0);
        }
    }
    return totalItems;
};

const _renderOrderSummarylines = (orderSummaryLines: IOrderSummaryLines | undefined, OrderSummaryItems: INodeProps,
    props: ICartViewProps, notificationItems: INotification[] | undefined, tax?: number, mattressRecyclingFee?: number): JSX.Element | null => {
    if (!orderSummaryLines) {
        props.context.telemetry.warning('OrderSummary content is empty, module wont render');
        return null;
    }
    getCartItemsQuantity(props.cartlines);
    yetToBeCalculated = props.resources.yetToBeCalculatedText.toLowerCase();
    const feeFromCart = props.data.cart.result?.cart.ExtensionProperties?.[0]?.Value?.DecimalValue!;
    recyclingFeeCart = feeFromCart;
    const finalRecyclingFee = feeFromCart ?  props.context.cultureFormatter.formatCurrency(feeFromCart) : _getRecyclingFee(orderSummaryLines).value;
    const { deliveryServices } = props.app.config;
    const checkCartLines: (ICartlinesViewProps[] | undefined)[] = [];
    deliveryServices.forEach((item: {serviceProductRecId: number}) => {
        const checkExists = props.cartlines?.filter(val => val.data?.cartline.ProductId === item.serviceProductRecId);
        if(checkExists?.length! > 0) {
            checkCartLines.push(checkExists);
        }
    });
    const cookies = new Cookies();
    const zipCode = cookies.get('zip_code');
    const itemsReduce = checkCartLines?.length;
    const totalCartlineItems = getCartItemsQuantity(props.cartlines);
    const zipCodeRef = React.useRef<HTMLSpanElement>(null);
    const zipCodeText = zipCodeRef.current?.innerHTML.split('').join(' ');
    return (
        <Node {...OrderSummaryItems}>
            {/* VSI Customization - Start {unknown} + {52133} */}
            {_renderPromoCode(props, notificationItems)}
            <div className='msc-order-view-summary'>
                <div className='msc-order-view-summary__item'>
                    <span className='sr-only' tabIndex={0}>{`${props.resources.itemsString}, ${totalCartlineItems > 0 ? (totalCartlineItems - itemsReduce) : totalCartlineItems}, ${formatNumber(getTotalPrice(orderSummaryLines))}`}</span>
                    <p tabIndex={-1} aria-hidden='true' className='msc-order-view-summary__label'>
                        <span className='msc-order-view-summary__text'>
                            {`${props.resources.itemsString} (${totalCartlineItems > 0 ? (totalCartlineItems - itemsReduce) : totalCartlineItems})`}
                        </span>
                        <span className='msc-order-view-summary__value'>
                            {formatNumber(getTotalPrice(orderSummaryLines))}
                        </span>
                    </p>
                </div>
                {orderSummaryLines.totalDiscounts &&
                    <div className='msc-order-view-summary__item'>
                        <span className='sr-only' tabIndex={0}>{`${props.resources.savingsString}, minus${formatNumber(getSavings(orderSummaryLines))}`}</span>
                        <p tabIndex={-1} aria-hidden='true' className='msc-order-view-summary__label'>
                            <span className='msc-order-view-summary__text'>{props.resources.savingsString}</span>
                            <span className='msc-order-view-summary__value msc-order-view-summary__discounted-amount'>
                                -{formatNumber(getSavings(orderSummaryLines))}
                            </span>
                        </p>
                    </div>
                }
                {!!(feeFromCart || _parseRecyclingFee(orderSummaryLines)) && props.calculateTaxOnCart &&
                    <div className='msc-order-view-summary__item'>
                        <span className='sr-only' tabIndex={0}>{`${_getRecyclingFee(orderSummaryLines).label}, ${finalRecyclingFee}`}</span>
                        <p aria-hidden='true' className='msc-order-view-summary__label'>
                            <span className='msc-order-view-summary__text'>{_getRecyclingFee(orderSummaryLines).label}</span>
                            <span className='msc-order-view-summary__value'>{finalRecyclingFee}</span>
                        </p>
                    </div>
                }
                <div className='msc-order-view-summary__item'>
                    <span className='sr-only' tabIndex={0}>{`${props.resources.taxString}, ${formatNumber(getTax(orderSummaryLines))}`}</span>
                    <p className='msc-order-view-summary__label'>
                        <span aria-hidden='true' className='msc-order-view-summary__text'>{props.resources.taxString}&nbsp;-&nbsp;</span>
                        <a
                            className='msc-order-view-summary__tax-button'
                            tabIndex={0}
                            role='button'
                            href={props.isTabletView ? '#cart-delivery-message' : 'javascript:void(0)'}
                            onClick={props.onOrderSummaryZipcodeClick}
                            aria-label={`${props.resources.changeZipCodeButtonText}, ${zipCodeText}`}
                        >
                            <span className='digits' ref={zipCodeRef} aria-hidden='true' aria-label={zipCodeText} tabIndex={-1}>{zipCode}</span>
                        </a>
                        <span aria-hidden='true' className='msc-order-view-summary__value'>{props.calculateTaxOnCart ? formatNumber(getTax(orderSummaryLines)): '-'}</span>
                    </p>
                </div>
            </div>
            <div className='msc-order-view-total'>
                <span className='sr-only' tabIndex={0}>{`${props.resources.subTotalString}, ${formatNumber(getFinalPrice(orderSummaryLines, _parseRecyclingFee(orderSummaryLines), props))}`}</span>
                <p tabIndex={-1} aria-hidden='true' className='msc-order-view-total__label'>
                    <span className='msc-order-view-summary__text'>{props.resources.subTotalString}</span>
                    <span className='msc-order-view-total__value'>
                        {formatNumber(getFinalPrice(orderSummaryLines, _parseRecyclingFee(orderSummaryLines), props))}
                    </span>
                </p>
            </div>
            {/* VSI Customization - End - */}
        </Node>
    );
};

/* VSI Customization - Start - */
const _renderPromoCode = (props: ICartViewProps, notificationItems: INotification[] | undefined): React.ReactNode => {
    const {
        data: {
            cart: { result: cart }
        },
        resources
    } = props;

    return cart && (
        <PromoCodeComponent
            cart={cart}
            promoCodeHeadingText={resources.promoCodeHeadingText}
            promoPlaceholderText={resources.promoPlaceholderText}
            promoCodeApplyButtonText={resources.promoCodeApplyButtonText}
            appliedPromoCodeHeadingText={resources.appliedPromoCodeHeadingText}
            collapseTimeOut={350}
            removePromoAriaLabelFormat={resources.removePromoAriaLabelFormat}
            removePromoText={resources.removePromoText}
            invalidPromoCodeErrorText={resources.invalidPromoCodeErrorText}
            failedToAddPromoCodeErrorText={resources.failedToAddPromoCodeErrorText}
            failedToRemovePromoCodeErrorText={resources.failedToRemovePromoCodeErrorText}
            duplicatePromoCodeErrorText={resources.duplicatePromotionErrorText}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            telemetryContent={props.telemetryContent}
            notifications={notificationItems}
            /* VSI Customization start resources for {#6563} + {#52133}*/
            mfrmExpiredPromoCodeErrorText={resources.mfrmExpiredPromoCodeErrorText}
            mfrmDoesNotApplyToCartPromoCodeErrorText={resources.mfrmDoesNotApplyToCartPromoCodeErrorText}
            mfrmCannotBeCombinedPromoCodeErrorText={resources.mfrmCannotBeCombinedPromoCodeErrorText}
            mfrmUsageLimitPromoCodeErrorText={resources.mfrmUsageLimitPromoCodeErrorText}
            appliedPromoText={resources.appliedPromoText}
            notAppliedPromoText={resources.notAppliedPromoText}
            /* VSI Cstomization end */
        />
    );
};
/* VSI Customization - End - */

const _renderPromotions = (promotions: ICartLineItemViewProps): JSX.Element | undefined => {
    return (
        <React.Fragment>
            <Node {...promotions.promotionMessageWrapper} >
                {promotions.promotionMessage}
                {promotions.promotionSeeAllLink}
                {promotions.promotionDialog}
            </Node>
        </React.Fragment>
    );
};

// tslint:disable-next-line: cyclomatic-complexity
const CartView: React.FC<ICartViewProps & IMfrmCartProps<IMfrmCartData>> = (props) => {
    /** VSI Customization - Start - */
    const { resources,_renderUpdateZipCode, _productDetails,  disableCheckoutButtonState, slots: { productCollection, buyNowPayLaterOptions }, config: { continueShoppingButtonTitle }, data: { cart: { result: cart } } } = props;
    const backToShoppingButton = <Button onClick={props.handleTealiumClick} className={'msc-cart__btn-backtoshopping'} title={continueShoppingButtonTitle}>{continueShoppingButtonTitle}</Button>;
    const isCartEmpty = cart && cart.cart.CartLines && cart.cart.CartLines.length === 0;
    const productAttributes = props.data.productAttributes.result;
    const Value:boolean = false;
    const getSavings = get(props.orderSummaryLineitems?.totalDiscounts, 'props.price');
    const savings = parseFloat(Math.abs(getSavings).toFixed(2));
    if(productAttributes && productAttributes.length > 0) {
        for(const productSpecItem of productAttributes) {

           // if(productSpecItem. === 'Core') {
           //     includeDeliveryInStepper = true;
           // }
           productSpecItem.attributeValue.find((shippingData) => {
               if(shippingData?.TextValue && shippingData.TextValue.trim().toLocaleLowerCase() === 'delivery') {
                   // includeDeliveryInStepper = true;
                   // props.showDeliveryStepper = true;
                   // this.setState({showDeliveryStepper:true});
               }
           });
        }
   }
    const checkoutDisableButton = !isCartEmpty ? <Button role='button' className={'msc-cart__btn-checkout disabled'} title={resources.checkoutButtonTitle} >{resources.checkoutButtonTitle}</Button> : null;
    /** VSI Customization - End - */
    const appendedClassName = props.cartlines && props.cartlines.length > 0 ? props.className : `${props.className} ms-cart-empty`;
    const stepClassName = 'mfrm-stepper__step';
    let steps: (() => { label: string; color: string; icon: JSX.Element; className: string; ariaLabel?: string; }[]) | { label: string; color: string; icon: JSX.Element; className: string; ariaLabel?: string }[];
    if (props.showDeliveryStepper) {
        steps = [
            {
                label: `${resources.stepperLabelCart}`,
                ariaLabel: `Step 1,`,
                color: '#D63426',
                icon: (<span tabIndex={-1} className='mfrm-circle--active' />),
                className: `${stepClassName}--inactive`
            },
            {
                label: `${resources.stepperLabelCustInfo}`,
                ariaLabel: `Step 2,`,
                color: '#B3B5B7',
                icon: (<span tabIndex={-1} className='mfrm-circle--inactive' />),
                className: `${stepClassName}--inactive`
            },
            {
                label: `${resources.stepperLabelDelivery}`,
                ariaLabel: `Step 3,`,
                color: '#B3B5B7',
                icon: (<span tabIndex={-1} className='mfrm-circle--inactive' />),
                className: `${stepClassName}--inactive`
            },
            {
                label: `${resources.stepperLabelPayment}`,
                ariaLabel: `Step 4,`,
                color: '#B3B5B7',
                icon: (<span tabIndex={-1} className='mfrm-circle--inactive' />),
                className: `${stepClassName}--inactive`
            }
        ];
    } else {
        steps = [
            {
                label: `${resources.stepperLabelCart}`,
                ariaLabel: `Step 1,`,
                color: '#D63426',
                icon: (<span tabIndex={-1} className='mfrm-circle--active' />),
                className: `${stepClassName}--inactive`
            },
            {
                label: `${resources.stepperLabelCustInfo}`,
                ariaLabel: `Step 2,`,
                color: '#B3B5B7',
                icon: (<span tabIndex={-1} className='mfrm-circle--inactive' />),
                className: `${stepClassName}--inactive`
            },
            {
                label: `${resources.stepperLabelPayment}`,
                ariaLabel: `Step 3,`,
                color: '#B3B5B7',
                icon: (<span tabIndex={-1} className='mfrm-circle--inactive' />),
                className: `${stepClassName}--inactive`
            }
        ];
    }
    const newTealiumData = `is_cart_active: ${
        isCartEmpty ? '"false"' : '"true"'
    } , new_site: "true" , page_context_type: "checkout" , page_type: "checkout" , page_name: "cart", cart_total_quantity: '${
        _productDetails?.quantity
    }' , cart_subtotal: '${_productDetails?.subTotal}' , cart_product_price: [${
        _productDetails?.cartProductPriceArray
    }] , cart_product_image_url: [${_productDetails?.productImageUrl}] , cart_product_id: [${
        _productDetails?.productIDsArray
    }] , cart_product_quantity: [${_productDetails?.productQuantity}] , cart_product_variant: [${
        _productDetails?.variantRecord
    }] , cart_product_category: [${_productDetails?.productCatgoryArray}] , cart_product_size: [${
        _productDetails?.productSize
    }] , cart_product_name: [${_productDetails?.productNameArray}] , cart_product_name_1: ${
        _productDetails?.productNameArray?.[0]
    } , cart_product_name_2: ${_productDetails?.productNameArray?.[1]} , cart_product_name_3: ${
        _productDetails?.productNameArray?.[2]
    } , cart_product_url: [${_productDetails?.productURL}] , cart_product_image_url_1: ${
        _productDetails?.productImageUrl?.[0]
    } , cart_product_image_url_2: ${_productDetails?.productImageUrl?.[1]} , cart_product_image_url_3: ${
        _productDetails?.productImageUrl?.[2]
    } , cart_url: '${_productDetails?.cartUrl}' , product_price: [${
        _productDetails?.productPriceArray
    }] , product_image_url: [${_productDetails?.productImageUrl}] , product_id: [${
        _productDetails?.productIDsArray
    }] , product_quantity: [${_productDetails?.productQuantity}] , product_variant: [${
        _productDetails?.variantRecord
    }] , product_category: [${_productDetails?.productCatgoryArray}] , product_size: [${
        _productDetails?.productSize
    }] , product_name: [${_productDetails?.productNameArray}] , product_url: [${_productDetails?.productURL}] , product_brand: [${_productDetails?.productBrandName}] , product_discount: [${_productDetails?.productDiscountArray}] , core_item_flag: "${_productDetails?.isCoreItem}" , funnel_type: "${_productDetails?.funnelType}"`;

    const newTealiumEmpty = `is_cart_active: '"false"' , new_site: "true" , page_context_type: "checkout" , page_type: "checkout"`;
    const realTealiumObject = isCartEmpty ? newTealiumEmpty : newTealiumData;
    const scriptHtml = `<script type="text/javascript">
    var utag_data = {${realTealiumObject} }
    </script>`;
    const { deliveryServices } = props.app.config;
    return (
        <div className={appendedClassName} id={props.id} {...props.renderModuleAttributes(props)}>
            {/** VSI Customization - Start - */}
            <div id='utagData2' dangerouslySetInnerHTML={{ __html: scriptHtml }} />
            <Node {...props.CartlinesWrapper}>
                <Node className={`msc-cart__head ${props.cartlines && props.cartlines.length > 0 ? '' : 'empty'}`}>
                    <div className='msc-cart__head-wrapper'>
                        {props.title}
                        {getSavings && <p>{format(resources.cartHeadingSavingLabel, props.context.cultureFormatter.formatCurrency(savings))}</p>}
                    </div>
                    {(props.cartlines && props.cartlines.length > 0) && props.isMobile && <div className='mfrm-stepper'> <Stepper activeStep={0} selectIndex={() => { }} steps={steps} /> </div>}
                    {props.promotionOptions && _renderPromotions(props.promotionOptions)}
                    {/* {props.cartlines && props.cartlines.length > 0 && backToShoppingButton} */}
                </Node>
                {/** VSI Customization - End - */}
                {props.showRemoveNotification && <span id='removeNotification' className='sr-only' tabIndex={0} role='alert' aria-live='assertive'>{format(resources.removedFromCartText, props.showProductTitle)}</span>}
                {props.cartlines && props.cartlines.length > 0 && _renderUpdateZipCode}
                {/** VSI Customization - Start - 14-12-2020 */}
                {/** Added a node and spans for static resource display */}
                {props.cartlines && props.cartlines.length > 0 &&
                    <Node className='msc-cart-title'>
                        <span>{resources.mfrmCartTitleProduct}</span>
                        <span>{resources.mfrmCartTitleQuantity}</span>
                        <span>{resources.mfrmCartTitlePrice}</span>
                    </Node>
                }
                {/** VSI Customization - End - 12-12-2020 */}
                {/** VSI Customization - Start - */}
                {Value}
                <Node className='msc-cartline-wraper-parent'>
                    {_renderCartlines(props.cartlines, resources.emptyCartText, props.CartlinesWrapper,
                        props.storeSelector, backToShoppingButton, props.waitingComponent, props.cartLoadingStatus, props.cartDataResult, productCollection, deliveryServices)}
                    {props.isDesktop && props.cartlines && props.cartlines.length > 0 && buyNowPayLaterOptions[0]}
                </Node>

                {/** VSI Customization - End - */}
            </Node>
            {props.orderSummaryHeading &&
                <Node className='msc-order-summary-wrapper-parent'>
                    {(props.cartlines && props.cartlines.length > 0) && props.isDesktop && <div className='mfrm-stepper'> <Stepper activeStep={0} selectIndex={() => { }} steps={steps} /> </div>}
                    <Node className='msc-order-summary-wrapper-spacer' />
                    <Node {...props.OrderSummaryWrapper}>
                        {/* VSI Customization - Start - */}
                        {/* {props.orderSummaryHeading} */}
                        {props.cartlines && props.cartlines.length > 0 && _renderUpdateZipCode}
                        {_renderOrderSummarylines(props.orderSummaryLineitems, props.OrderSummaryItems, props, props.data.notifications.result)}
                        {/* VSI Customization - End - */}
                        {_renderErrorBlock(props.OrderSummaryErrors)}
                        {/** VSI Customization - Start - */}
                        {<> {!disableCheckoutButtonState ? props.checkoutAsSignInUserButton : checkoutDisableButton} </>}
                        {/* props.checkoutAsGuestButton */}
                        {/** VSI Customization - End - */}
                        {props.createTemplateFromCartButton}
                    </Node>
                </Node>
            }
            {props.isMobile && props.cartlines && props.cartlines.length > 0 && buyNowPayLaterOptions[0]}
            {<Node className='msc-cart__btn-checkout-wrapper'> {!disableCheckoutButtonState ? props.checkoutAsSignInUserButton : checkoutDisableButton}</Node>}
            {props.storeSelector}
        </div>
    );
};

export default CartView;